import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	HostBinding,
	OnDestroy,
	OnInit,
	ViewEncapsulation,
} from "@angular/core";
import { UntypedFormBuilder, Validators } from "@angular/forms";
import { select, Store } from "@ngrx/store";
import { AlertButtonColor } from "@obg/mat.ui.core";
import {
	CommonState,
	MULTI_SESSION_LIMIT_MESSAGE_CONTEXT,
	MultiSessionLimit,
	MultiSessionLimitAction,
	MultiSessionLimitResponse,
	MultiSessionLimits,
	MultiSessionLimitSelector,
} from "@obg/ngx.common";
import {
	AbstractTranslatableAlertService,
	AbstractTranslatableSnackBarService,
	MessageService,
	MessageType,
} from "@obg/ngx.core";
import { TranslationGroup } from "@obg/ngx.core.content";
import { sessionLimitAmountValidator } from "@obg/ngx.ui.common";
import { pickBy } from "lodash-es";
import { merge, Subscription } from "rxjs";
import { take, tap } from "rxjs/operators";

import { ResponsibleGamingFormService } from "../../containers/responsible-gaming-form.service";

import {
	KsaSessionLimitFormGroup,
	KsaSessionLimitPeriod,
	KsaSessionLimitPeriodTranslationsEnum,
	KsaSessionLimitSuggestionAmounts,
	KsaSessionLimitTemplate,
} from "./ksa-session-limit.container.model";

const TRANSLATION_PREFIX = "multi-session-limit";
const KSA_SESSION_LIMIT_ALERT_ID = "KSA_SESSION_LIMIT_ALERT_ID";

@Component({
	selector: "obg-m-ksa-session-limit-container",
	templateUrl: "./ksa-session-limit.container.html",
	styleUrls: ["./ksa-session-limit.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class KsaSessionLimitContainer implements OnInit, OnDestroy {
	@HostBinding("class.obg-m-ksa-session-limit") hostClass = true;

	readonly sessionLimitPeriodControls = KsaSessionLimitPeriod;

	isSettingBusy = false;
	currentSessionLimit: MultiSessionLimits | undefined;
	currentSessionLimitConverted: KsaSessionLimitTemplate | undefined;
	upcomingSessionLimit: KsaSessionLimitTemplate | undefined;

	formGroup: KsaSessionLimitFormGroup = this.formBuilder.group({
		sessionLimitAmount: this.formBuilder.control("", [sessionLimitAmountValidator]),
		sessionLimitPeriod: this.formBuilder.control(KsaSessionLimitPeriod.Daily, [Validators.required]),
	}) as KsaSessionLimitFormGroup;
	isSessionLimitEdited = false;
	placeholder = "24";
	private data$$: Subscription | undefined;
	private isFormDirty$$: Subscription | undefined;

	constructor(
		private multiSessionLimitSelector: MultiSessionLimitSelector,
		private multiSessionLimitAction: MultiSessionLimitAction,
		private formBuilder: UntypedFormBuilder,
		private store: Store<CommonState>,
		private alertService: AbstractTranslatableAlertService,
		private snackBarService: AbstractTranslatableSnackBarService,
		private changeDetector: ChangeDetectorRef,
		private messageService: MessageService,
		private responsibleGamingFormService: ResponsibleGamingFormService
	) {}

	ngOnInit() {
		this.store.dispatch(this.multiSessionLimitAction.get());

		const sessionLimits$ = this.store.pipe(
			select(this.multiSessionLimitSelector.get()),
			tap((sessionLimit) => {
				this.currentSessionLimit = sessionLimit;
				this.currentSessionLimitConverted = undefined;
				this.upcomingSessionLimit = undefined;
				if (sessionLimit?.current) {
					this.currentSessionLimitConverted = this.mapSessionLimits(sessionLimit.current);
				}

				if (sessionLimit?.upcoming) {
					this.upcomingSessionLimit = this.mapSessionLimits(sessionLimit.upcoming);
				}
			})
		);

		const sessionLimitUpdate$ = this.messageService
			.consume$({
				context: MULTI_SESSION_LIMIT_MESSAGE_CONTEXT.update,
			})
			.pipe(
				tap((message) => {
					this.isSettingBusy = false;
					this.formGroup.setValue({
						sessionLimitAmount: "",
						sessionLimitPeriod: KsaSessionLimitPeriod.Daily,
					});
					if (message[0].type === MessageType.Success) {
						this.updateSuccess("update_success");
						return;
					}
					this.updateFail();
				})
			);

		const sessionLimitPeriodControlChanges$ = this.formGroup.controls.sessionLimitPeriod.valueChanges.pipe(
			tap((value: KsaSessionLimitPeriod) => {
				this.formGroup.controls.sessionLimitAmount.reset();
				this.placeholder =
					KsaSessionLimitSuggestionAmounts[
						KsaSessionLimitPeriod[value] as keyof typeof KsaSessionLimitSuggestionAmounts
					].toString();
			})
		);

		const isBusy$ = this.store.pipe(
			select(this.multiSessionLimitSelector.isBusy()),
			tap((isBusy) => (this.isSettingBusy = isBusy))
		);

		const resetForm$ = this.responsibleGamingFormService.resetForm$.pipe(
			tap(() => {
				this.formGroup?.reset({
					sessionLimitAmount: "",
					sessionLimitPeriod: KsaSessionLimitPeriod.Daily,
				});
			})
		);

		this.data$$ = merge(
			sessionLimits$,
			sessionLimitUpdate$,
			sessionLimitPeriodControlChanges$,
			isBusy$,
			resetForm$
		).subscribe(() => this.changeDetector.markForCheck());

		this.isFormDirty$$ = this.formGroup.valueChanges.subscribe(() =>
			this.responsibleGamingFormService.sendIsFormDirty(this.formGroup.dirty)
		);
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
		this.isFormDirty$$?.unsubscribe();
	}

	editSessionLimit() {
		this.isSessionLimitEdited = true;
	}

	undoUpcomingLimit() {
		if (!this.currentSessionLimit?.current) {
			return;
		}

		const title = `${TRANSLATION_PREFIX}.undo-limit-confirmation-title`;
		const message = `${TRANSLATION_PREFIX}.undo-limit-confirmation-caption`;
		const currentLimitPayload = this.getSessionLimitPeriod(this.currentSessionLimit.current);
		this.submitSessionLimit(title, message, currentLimitPayload);
	}

	submitNewLimit() {
		const title = `${TRANSLATION_PREFIX}.confirmation-title`;
		const message = `${TRANSLATION_PREFIX}.confirmation-caption`;
		this.submitSessionLimit(title, message);
	}

	submitSessionLimit(title: string, message: string, currentLimitPayload?: MultiSessionLimit) {
		this.alertService.open(
			{
				id: KSA_SESSION_LIMIT_ALERT_ID,
				title,
				message,
				hideClose: true,
				dialogConfig: {
					disableClose: true,
				},
				mustBeInteracted: true,
				buttons: [
					{
						text: "general.cancel",
						color: AlertButtonColor.Primary,
					},
					{
						text: "general.confirm",
						handler: () => {
							this.store.dispatch(
								this.multiSessionLimitAction.update(
									currentLimitPayload
										? currentLimitPayload
										: this.getMultiSessionLimitValues(
												this.formGroup.controls.sessionLimitAmount.value,
												this.formGroup.controls.sessionLimitPeriod
													.value as KsaSessionLimitPeriod
											)
								)
							);
							this.isSettingBusy = true;
							this.changeDetector.markForCheck();
						},
					},
				],
			},
			[TranslationGroup.Common]
		);
	}

	private updateFail() {
		this.snackBarService
			.openSnackBar$(
				{
					message: "general.e",
					duration: 4000,
					action: "general.close",
					type: MessageType.Error,
				},
				[TranslationGroup.Common]
			)
			.pipe(take(1))
			// eslint-disable-next-line rxjs/no-ignored-subscription
			.subscribe();
	}

	private updateSuccess(suffix: string) {
		this.snackBarService
			.openSnackBar$(
				{
					message: `${TRANSLATION_PREFIX}.m_multi_session_limit_${suffix}`,
					duration: 4000,
					action: "general.close",
					type: MessageType.Success,
				},
				[TranslationGroup.Common]
			)
			.pipe(take(1))
			// eslint-disable-next-line rxjs/no-ignored-subscription
			.subscribe();
	}

	private mapSessionLimits(sessionLimit: MultiSessionLimitResponse) {
		return {
			...this.getSessionLimitPeriodTranslation(this.getSessionLimitPeriod(sessionLimit)),
			activeFrom: sessionLimit.activeFrom,
		};
	}

	private getSessionLimitPeriod(sessionLimit: MultiSessionLimitResponse): MultiSessionLimit {
		return pickBy(
			sessionLimit,
			(value, key: keyof MultiSessionLimitResponse) => value !== 0 && key !== "activeFrom"
		);
	}

	private getSessionLimitPeriodTranslation(sessionLimitPeriod: MultiSessionLimit) {
		const key = Object.keys(sessionLimitPeriod)[0] as KsaSessionLimitPeriodTranslationsEnum;
		const period = Object.keys(KsaSessionLimitPeriodTranslationsEnum)
			.find(
				(x: keyof typeof KsaSessionLimitPeriodTranslationsEnum) =>
					KsaSessionLimitPeriodTranslationsEnum[x] === key
			)!
			.toLowerCase();
		return { limitValue: sessionLimitPeriod[key]!, limitPeriod: period };
	}

	private getMultiSessionLimitValues(sessionLimitAmount: number, sessionLimitPeriod: KsaSessionLimitPeriod) {
		const periodName = KsaSessionLimitPeriod[
			sessionLimitPeriod
		] as keyof typeof KsaSessionLimitPeriodTranslationsEnum;
		const period = KsaSessionLimitPeriodTranslationsEnum[periodName];
		return { [period]: sessionLimitAmount };
	}
}
